import { useContext, useEffect, useMemo } from 'react';
import { isRunActivelySettling } from '../types/Run';
import { ReprocessContext } from '../providers/reprocessContext';

interface WatchableRun {
  id?: string | null;
  run_status?: number | null;
}

// The batch progress endpoint rejects more than 200 ids outright, and a 400
// would stall the whole watch. Stay well under it: any overflow is simply left
// to the list's own poll, which skips watched runs and so covers the remainder.
const MAX_WATCHED = 100;

/**
 * Hand every run that is still working towards a terminal state to the
 * ReprocessProvider's `/runs/progress` watcher.
 *
 * The signal is `run_status` alone: a run that is uploaded/matched or actively
 * processing is still working towards a terminal state, whether that is a fresh
 * upload's first pass or a reprocess. This covers reload recovery too — a
 * reprocess in flight has had `run_status` stamped to PROCESSING by the
 * pipeline, so a reload finds it here.
 *
 * It used to also watch any run with `processing_stage` populated. That column
 * is gone: progress lives on the job row now, and the run payload no longer
 * carries it (migration c15041f99844). The one case that check caught and this
 * one does not is the gap between a reprocess being ACCEPTED and the worker
 * stamping PROCESSING — usually seconds. The list's own 5 s poll picks the run
 * up as soon as that stamp lands, so the cost is a brief delay before the
 * indicator appears on a reload, not a run that is never watched.
 *
 * Planned (bare-`CREATED`) runs are excluded: they sit in the settling band by
 * design and never advance on their own, so watching them would poll forever
 * for nothing.
 *
 * The provider polls them on one batched request and invalidates the run and
 * session caches when they finish, so every list showing that run refreshes —
 * including the ones (SessionView, SessionSimple) that have no poll of their
 * own and would otherwise sit on a stale "Processing…" until a hard refresh.
 *
 * Safe to call from several mounted pages at once: watches are keyed by run id
 * and probe-mode registration never disturbs an in-flight one.
 */
export function useWatchSettlingRuns(runs: WatchableRun[] | null | undefined): void {
  const reprocess = useContext(ReprocessContext);
  const watchRuns = reprocess?.watchRuns;

  const ids = useMemo(
    () => (runs ?? [])
      .filter((r) => r && isRunActivelySettling(r.run_status))
      .map((r) => r.id)
      .filter((id): id is string => !!id)
      .slice(0, MAX_WATCHED)
      .join(','),
    [runs],
  );

  useEffect(() => {
    if (!watchRuns || !ids) return;
    watchRuns(ids.split(','));
  }, [ids, watchRuns]);
}

/** Flatten a session payload's configs into its runs, for `useWatchSettlingRuns`. */
export function sessionRuns<T = any>(session: { configs?: { runs?: T[] }[] } | null | undefined): T[] {
  return (session?.configs ?? []).flatMap((c) => c?.runs ?? []);
}
